/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import {MRT_ColumnDef} from "material-react-table";
import {Box, Dialog, FormControlLabel, IconButton, Radio, TextField} from "@mui/material";
import {Add as AddIcon, Edit as EditIcon} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {MultiPageWizard} from "../../../components/MultiPageWizard/MultiPageWizard";
import Loader from "../../../components/Loader/Loader";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import {v4 as uuidv4} from 'uuid';
import {usePostRequest} from "../../../../api/helper";
import {COLUMN_NAMES, DELETE_RWO_ENDPOINT, DELETE_RWO_VARIANT_TITLE_TEXT, DELETE_RWO_VARIANT_WARNING_TEXT, VALUE_KEYS} from "../constants";
import {DeleteRwoDialog} from "../components/DeleteRwoDialog/DeleteRwoDialog";
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";

const SignProperties = (props: any) => {

  const [generalData, setGeneralData] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [editableCell, setEditableCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string | null }>({});
  const [temporaryValue, setTemporaryValue] = useState<string>("");
  const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedSignSize, setSelectedSignSize] = useState<any | null>(null);
  const [selectedUsageSpeed, setSelectedUsageSpeed] = useState<any | null>(null);
  let {onSignDataUpdate, onSignTablePropertiesUpdate, wipeSignPropertiesTable, resetTable} = props;
  const [localResetTable, setLocalResetTable] = useState(resetTable);


  useEffect(() => {
    if (props?.object?.signVariants) {
      // Flatten the signVariants from the matching objects
      let signVariants = props?.object?.signVariants

      // Get Picklist-SignSizes and Picklist-SignUsageSpeed from picklistmatchingObjects
      const signSizes = props?.object?.signSizes
      const usageSpeeds = props?.object?.usageSpeeds

      // Create maps for quick lookup
      const sizeMap = new Map(signSizes.map((size: { sizeID: any; }) => [size?.sizeID, size]));
      const speedMap = new Map(usageSpeeds.map((speed: { speedID: any; }) => [speed.speedID, speed]));

      // Modify each signVariant by adding details from sizeMap and speedMap
      const signVariantsModified = signVariants.map((variant: { sizeID: string; usageSpeedID: string; }) => {
        const sizeDetails: any = sizeMap.get(variant?.sizeID) || {};
        const speedDetails: any = speedMap.get(variant?.usageSpeedID) || {};

        // Combine existing variant details with size and speed details
        return {
          ...variant,
          ...sizeDetails,
          ...speedDetails
        };
      });

      // Set the general data with modified signVariants
      setGeneralData(signVariantsModified);
    } else {
      setGeneralData([]);
    }
  }, [props?.object?.signVariants]);


  useEffect(() => {
    if (localResetTable === false || localResetTable === undefined) {
      if (onSignDataUpdate) {
        onSignDataUpdate({signVariants: generalData});
      }
    } else if (localResetTable === true) {
      setGeneralData([]);
    }

  }, [generalData])


// React to the button or event triggering state change
  useEffect(() => {
    if (onSignTablePropertiesUpdate) {
      onSignTablePropertiesUpdate();
    }
  }, [onSignTablePropertiesUpdate]);


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setEditableCell(null);
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
    setSelectedSignSize(null);
    setSelectedUsageSpeed(null);
    setIsEditMode(false);
  };

  const handleCellClick = (rowIndex: number, columnId: string) => {
    if (columnId === "comments") {
      setEditableCell({rowIndex, columnId});
      setTemporaryValue(generalData[rowIndex][columnId]);
    }
  };

  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTemporaryValue(e.target.value);
    setValidationErrors((prev) => ({
      ...prev,
      [`row-${editableCell?.rowIndex}`]: null,
    }));
  };

  const handleCellBlur = (rowIndex: number, columnId: string) => {
    const updatedData = generalData.map((row, index) =>
      index === rowIndex ? {...row, [columnId]: temporaryValue} : row
    );
    setGeneralData(updatedData);
    setEditableCell(null);
  };

  const handleEditClick = (rowData: any, rowIndex: number) => {
    setLocalResetTable(false);
    setSelectedRowData({...rowData, rowIndex});
    setIsEditMode(true);
    setIsWizardOpen(true);
  };

  const handleSignSizeSelect = (size: any) => {
    setSelectedSignSize(size);
  };

  const handleUsageSpeedSelect = (speed: any) => {
    setSelectedUsageSpeed(speed);
  };


  const handleFinish = () => {

    if (isEditMode && selectedRowData?.rowIndex !== undefined) {
      const updatedData = generalData.map((row, index) => {
        if (index === selectedRowData.rowIndex) {
          return {
            ...row,
            signSizeVariant: selectedSignSize?.signShape || row.signSizeVariant,
            width: selectedSignSize?.["overallWidth(m)"] || row.width,
            height: selectedSignSize?.["overallHeight(m)"] || row.height,
            sizeID: selectedSignSize?.sizeID || row?.sizeID,
            recommendedEstimatedUsageSpeed: selectedUsageSpeed?.recommendedEstimatedUsageSpeed || row.recommendedEstimatedUsageSpeed,
            usageSpeedID: selectedUsageSpeed?.speedID || row?.usageSpeedID,
            knownOdds: selectedSignSize?.knownOdds,
            signBasis: selectedSignSize?.signBasis,
          };
        }
        return row;
      });
      setGeneralData(updatedData);
    } else {
      const newRow = {
        signBasis: selectedSignSize?.signBasis || "N/A",
        signSizeVariant: selectedSignSize?.signShape || "N/A",
        width: selectedSignSize?.["overallWidth(m)"] || "N/A",
        height: selectedSignSize?.["overallHeight(m)"] || "N/A",
        sizeID: selectedSignSize?.sizeID || "N/A",
        recommendedEstimatedUsageSpeed: selectedUsageSpeed?.recommendedEstimatedUsageSpeed || "N/A",
        usageSpeedID: selectedUsageSpeed?.speedID || "N/A",
        knownOdds: selectedSignSize?.knownOdds || "N/A",
        comments: "",
        description: "",
        id: uuidv4(), // TODO: In future, should do this in backend so that we keep the id generation as just one function in backend (then if it changes, we only have to change one place).
      };
      setGeneralData([...generalData, newRow]);
    }
    handleCloseWizard();
  };

  const [isLoadingDeleteRwoVersion, setIsLoadingDeleteRwoVersion] = useState(false);
  const deleteRwoVersionHandle = usePostRequest(DELETE_RWO_ENDPOINT)
  const [isDeleteRwoVersionDialogOpen, setIsDeleteRwoVersionDialogOpen] = useState<any>(null);
  const onClickDeleteRwoVersion = (rwoId: string, variantId: string) => {
    setIsDeleteRwoVersionDialogOpen({rwoId, variantId})
  }
  /**
   * Handles the deletion of an rwo version.
   * @param rwoId - The ID of the rwo to delete the variant from.
   * @param variantId - The ID of the variant to delete.
   */
  const handleDeleteRwoVersion = (rwoId: string, variantId: string) => {
    // Remove this variant from general data
    const updatedData = generalData.filter((row) => row.id !== variantId);
    setGeneralData(updatedData);
    setIsDeleteRwoVersionDialogOpen(null)
  }

  const commonColumns: MRT_ColumnDef<any>[] = [
    {
      id: "width",
      accessorFn: (row: any) => parseFloat(row.width).toFixed(4) ?? '',
      header: "Width (meters)",
    },
    {
      id: "height",
      accessorFn: (row: any) => parseFloat(row.height).toFixed(4) ?? '',
      header: "Height (meters)",
    },
    {
      id: "knownOdds",
      accessorKey: "knownOdds",
      header: "Known ODDs",
    },
    {
      id: "recommendedEstimatedUsageSpeed",
      accessorKey: "recommendedEstimatedUsageSpeed",
      header: "Recommended Usage/Speed",
    },
    {
      id: "signBasis",
      accessorKey: "signBasis",
      header: "Sign Basis",
    },
    {
      id: VALUE_KEYS.ID,
      accessorKey: VALUE_KEYS.ID,
      header: COLUMN_NAMES.ID,
      size: 300,
    },
    {
      id: "comments",
      accessorKey: "comments",
      header: "Comments",
      Cell: ({cell, row}: any) => (
        editableCell?.rowIndex === row.index && editableCell?.columnId === "comments" ? (
          <TextField
            value={temporaryValue}
            onChange={handleCellChange}
            onBlur={() => handleCellBlur(row.index, "comments")}
            autoFocus
          />
        ) : (
          <div onClick={() => handleCellClick(row.index, "comments")} style={{cursor: "pointer", minWidth: "40px", padding: "4px"}}>
            {cell.getValue() || ""}
          </div>
        )
      ),
    },
    {
      id: "actions",
      header: "Actions",
      accessorKey: "actions",
      Cell: ({row}: any) => (
        <div>
          <IconButton onClick={() => handleEditClick(row.original, row.index)}>
            <EditIcon/>
          </IconButton>
          <IconButton
            onClick={() => {
              // TODO: Maybe don't implement this.
              onClickDeleteRwoVersion(props.object?.id, row.original.id)
            }}
          >
            <DeleteIcon/>
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div>
      <TabPanel value={selectedTab} index={0}>
        <div style={{display: 'flex', justifyContent: "flex-end", paddingBottom: "2px"}}>
          <IconButton onClick={() => {
            setIsWizardOpen(true);
            setIsEditMode(false);
            setLocalResetTable(false)
          }}>
            <AddIcon/>
          </IconButton>
        </div>
        <ArgosTableSimple
          enableTopToolbar={false}
          columns={commonColumns}
          data={generalData}
          initialState={{density: "compact"}}
          enableHiding={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableColumnFilters={false}
          enableGlobalFilter={false}
        />
      </TabPanel>
      <Dialog open={isWizardOpen} onClose={handleCloseWizard} maxWidth="md" fullWidth>
        <ArgosFlexbox vertical style={{flex: 1, overflow: "auto"}}>
          <Loader isLoading={false}>
            <MultiPageWizard
              steps={["Step 1", "Step 2", "Step 3"]}
              onFinish={handleFinish}
              isNextDisabled={[!selectedSignSize, !selectedUsageSpeed, false]}
              isFinishLoading={false}
              onClickClose={handleCloseWizard}
            >
              {/* Step 1: Select Sign Size */}
              <div style={{padding: '20px', maxHeight: '300px', overflowY: 'auto'}}>
                <h2 style={{fontSize: '1.5em'}}>Step 1: Select Sign Size</h2>
                <ArgosTableSimple
                  enableTopToolbar={false}
                  columns={[
                    {
                      id: "select",
                      Cell: ({row}: any) => (
                        <FormControlLabel
                          control={
                            <Radio
                              checked={selectedSignSize?.sizeID === row?.original?.sizeID}
                              onChange={() => handleSignSizeSelect(row.original)}
                            />
                          }
                          label=""
                        />
                      ),
                      header: "Select",
                    },
                    {id: "knownOdds", header: "Known ODDs", accessorKey: 'knownOdds'},
                    {id: "overallWidth", header: "Width (m)", accessorFn: (row: any) => parseFloat(row["overallWidth(m)"])?.toFixed(4) ?? ''},
                    {id: "overallHeight", header: "Height (m)", accessorFn: (row: any) => parseFloat(row["overallHeight(m)"])?.toFixed(4) ?? ''},
                  ]}
                  data={props?.object?.signSizes}
                  initialState={{density: "compact"}}
                  enablePagination={false}
                  enableBottomToolbar={false}
                  enableHiding={false}
                />
              </div>

              {/* Step 2: Select Usage Speed */}
              <div style={{padding: '20px', maxHeight: '300px', overflowY: 'auto'}}>
                <h2 style={{fontSize: '1.5em'}}>Step 2: Select Usage Speed</h2>
                <ArgosTableSimple
                  enableTopToolbar={false}
                  columns={[
                    {
                      id: "select",
                      Cell: ({row}: any) => (
                        <FormControlLabel
                          control={
                            <Radio
                              checked={selectedUsageSpeed?.speedID === row.original.speedID}
                              onChange={() => handleUsageSpeedSelect(row.original)}
                            />
                          }
                          label=""
                        />
                      ),
                      header: "Select",
                    },
                    {
                      id: "recommendedEstimatedUsageSpeed",
                      header: "Recommended Estimated Usage Speed",
                      accessorKey: "recommendedEstimatedUsageSpeed",
                    },
                    {
                      id: "source",
                      header: "Source",
                      accessorKey: "source",
                    },
                  ]}
                  data={props?.object?.usageSpeeds}
                  initialState={{density: "compact"}}
                  enablePagination={false}
                  enableBottomToolbar={false}
                  enableHiding={false}
                />
              </div>

              {/* Step 3: Review Selection */}
              <div
                style={{
                  padding: '20px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  marginBottom: '20px',
                }}
              >
                <h2 style={{fontSize: '1.5em', marginBottom: '20px'}}>Step 3: Review Selection</h2>

                {/* Displaying Selected Sign Size */}
                <div style={{marginBottom: '20px'}}>
                  <strong>Selected Sign Size:</strong>
                  <ul style={{paddingLeft: '20px', listStyleType: 'circle'}}>
                    <li><strong>Known ODDs:</strong> {selectedSignSize?.knownOdds || "N/A"}</li>
                    <li><strong>Overall Width (m):</strong> {selectedSignSize?.['overallWidth(m)'] || "N/A"}</li>
                    <li><strong>Overall Height (m):</strong> {selectedSignSize?.['overallHeight(m)'] || "N/A"}</li>
                  </ul>
                </div>

                {/* Displaying Selected Usage Speed */}
                <div>
                  <strong>Selected Usage Speed:</strong>
                  <ul style={{paddingLeft: '20px', listStyleType: 'circle'}}>
                    <li><strong>Recommended Estimated Usage Speed:</strong> {selectedUsageSpeed?.recommendedEstimatedUsageSpeed || "N/A"}</li>
                    <li><strong>Source:</strong> {selectedUsageSpeed?.source || "N/A"}</li>
                  </ul>
                </div>
              </div>
            </MultiPageWizard>
          </Loader>
        </ArgosFlexbox>
      </Dialog>

      <DeleteRwoDialog
        isDeleteRwoVersionDialogOpen={isDeleteRwoVersionDialogOpen}
        setIsDeleteRwoVersionDialogOpen={setIsDeleteRwoVersionDialogOpen}
        handleDeleteRwoVersion={() => handleDeleteRwoVersion(isDeleteRwoVersionDialogOpen?.rwoId, isDeleteRwoVersionDialogOpen?.variantId)}
        isLoadingDeleteRwoVersion={isLoadingDeleteRwoVersion}
        titleText={DELETE_RWO_VARIANT_TITLE_TEXT}
        warningText={DELETE_RWO_VARIANT_WARNING_TEXT}
      />
    </div>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const {children, value, index, ...other} = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
};

export default SignProperties;